/**
 * Dynamic Suggestions Hook — Web
 *
 * Manages AI-generated prompt suggestions based on context:
 * - Screen capture analysis
 * - Recent chat messages
 * - Caching (5-minute TTL) to avoid redundant API calls
 * - Loading states and error handling
 *
 * Ported from the desktop hook so web + extension sidepanel get the same
 * "Get AI-suggested prompts" lightbulb behavior as desktop.
 */

import { useCallback, useState } from 'react';

import { createLogger } from '@ctrl-enter/tools/utils';
import type { DynamicSuggestion, SuggestionCache } from '@ctrl-enter/types';

import { usePromptLibraryStore } from '../stores/prompt-library-store';
import { generateContextHash, isCacheValid } from '../utils/prompt-utils';
import { toast } from '../utils/toast';
import { useGenerateSuggestionsMutation } from './use-gateway-queries';

const logger = createLogger('client:hook:use-dynamic-suggestions');

type UseDynamicSuggestionsProps = {
  screenCaptureId?: string;
  recentMessages?: string[];
};

type UseDynamicSuggestionsReturn = {
  suggestions: DynamicSuggestion[];
  isLoading: boolean;
  error: string | null;
  generateSuggestions: () => Promise<void>;
  clearSuggestions: () => void;
};

const CACHE_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

export function useDynamicSuggestions({
  screenCaptureId,
  recentMessages = [],
}: UseDynamicSuggestionsProps): UseDynamicSuggestionsReturn {
  const [suggestions, setSuggestions] = useState<DynamicSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);

  const suggestionCache = usePromptLibraryStore(state => state.suggestionCache);
  const setSuggestionCache = usePromptLibraryStore(state => state.setSuggestionCache);
  const isLoading = usePromptLibraryStore(state => state.isSuggestionsLoading);
  const setIsLoading = usePromptLibraryStore(state => state.setIsSuggestionsLoading);

  const { mutateAsync: generateSuggestionsAPI } = useGenerateSuggestionsMutation();

  const generateSuggestions = useCallback(async () => {
    setError(null);

    const contextHash = generateContextHash(screenCaptureId, recentMessages);

    // Fast path — return cached suggestions if still fresh and context-equivalent
    if (
      suggestionCache &&
      suggestionCache.contextHash === contextHash &&
      isCacheValid(suggestionCache.timestamp, CACHE_MAX_AGE_MS)
    ) {
      logger.info('💡 [SUGGESTIONS] Using cached suggestions');
      setSuggestions(suggestionCache.suggestions);
      return;
    }

    try {
      setIsLoading(true);
      logger.info('💡 [SUGGESTIONS] Generating AI suggestions...');

      const response = await generateSuggestionsAPI({
        body: {
          screenCaptureId,
          recentMessages,
        },
      });

      if (response.status === 200 && response.body.suggestions) {
        const apiSuggestions = response.body.suggestions;

        const cache: SuggestionCache = {
          contextHash,
          suggestions: apiSuggestions,
          timestamp: Date.now(),
          screenCaptureId,
        };

        setSuggestionCache(cache);
        setSuggestions(apiSuggestions);

        logger.info(`💡 [SUGGESTIONS] Generated ${apiSuggestions.length} suggestions`);
        if (response.body.contextAnalysis) {
          logger.info(`💡 [SUGGESTIONS] Context: ${response.body.contextAnalysis}`);
        }
      } else {
        throw new Error('Invalid response from suggestions API');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate suggestions';
      setError(errorMessage);
      toast.error('Suggestion Error', errorMessage);
      logger.error('💡 [SUGGESTIONS] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    screenCaptureId,
    recentMessages,
    suggestionCache,
    setSuggestionCache,
    setIsLoading,
    generateSuggestionsAPI,
  ]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setError(null);
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    generateSuggestions,
    clearSuggestions,
  };
}
